import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchPayPeriods,
  fetchEmployees,
  fetchDailyTips,
  fetchDailySales,
  fetchEvents,
  fetchKitchenTipouts,
  fetchSettings,
  getSettingValue,
  upsertDailyTipsBatch,
  upsertDailySales,
  upsertEvent,
  deleteEvent,
  replaceKitchenTipoutsForEvent,
} from "../tipsApi";
import type { PayPeriod, Employee, DailyTip, DailySales, TipEvent, KitchenTipout } from "../types";
import { DayEditor } from "../components/DayEditor";
import type { KitchenTipEntry } from "../components/DayEditor";
import { PayPeriodSummary } from "../components/PayPeriodSummary";
import { employeesForPeriod } from "../employeePeriod";

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function datesInRange(start: string, end: string): string[] {
  const result: string[] = [];
  let cur = start;
  while (cur <= end) {
    result.push(cur);
    cur = addDays(cur, 1);
  }
  return result;
}

function fmtRange(pp: PayPeriod): string {
  const s = new Date(pp.start_date + "T00:00:00");
  const e = new Date(pp.end_date + "T00:00:00");
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(s)} – ${fmt(e)}, ${e.getFullYear()}`;
}

export function TipEntryView() {
  const [periods, setPeriods] = useState<PayPeriod[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [allKitchenEmployees, setAllKitchenEmployees] = useState<Employee[]>([]);
  const [tips, setTips] = useState<DailyTip[]>([]);
  const [sales, setSales] = useState<DailySales[]>([]);
  const [events, setEvents] = useState<TipEvent[]>([]);
  const [kitchenTipoutRecords, setKitchenTipoutRecords] = useState<KitchenTipout[]>([]);
  const [feePct, setFeePct] = useState(5.0);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selected = useMemo(
    () => periods.find((p) => p.id === selectedId) ?? null,
    [periods, selectedId],
  );

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [pp, emp, kitEmp, settings] = await Promise.all([
        fetchPayPeriods(),
        fetchEmployees("bartender"),
        fetchEmployees("kitchen"),
        fetchSettings(),
      ]);
      setPeriods(pp);
      setAllEmployees(emp);
      setAllKitchenEmployees(kitEmp);
      setFeePct(parseFloat(getSettingValue(settings, "processing_fee_pct", "5.0")));
      if (pp.length > 0 && !selectedId) setSelectedId(pp[0].id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);

  const loadPeriodData = useCallback(async () => {
    if (!selected) return;
    try {
      const [t, s, ev, kt] = await Promise.all([
        fetchDailyTips(selected.start_date, selected.end_date),
        fetchDailySales(selected.start_date, selected.end_date),
        fetchEvents(selected.start_date, selected.end_date),
        fetchKitchenTipouts(selected.start_date, selected.end_date),
      ]);
      setTips(t);
      setSales(s);
      setEvents(ev);
      setKitchenTipoutRecords(kt);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [selected]);

  useEffect(() => { loadPeriodData(); }, [loadPeriodData]);

  const dates = useMemo(
    () => (selected ? datesInRange(selected.start_date, selected.end_date) : []),
    [selected],
  );

  const employees = useMemo(
    () => employeesForPeriod(allEmployees, selected, tips.map((t) => t.employee_id)),
    [allEmployees, selected, tips],
  );

  const kitchenEmployees = useMemo(
    () =>
      employeesForPeriod(
        allKitchenEmployees,
        selected,
        kitchenTipoutRecords.map((k) => k.employee_id).filter((id): id is string => Boolean(id)),
      ),
    [allKitchenEmployees, selected, kitchenTipoutRecords],
  );

  const tipsByDate = useMemo(() => {
    const m = new Map<string, Map<string, { hours_worked: number; raw_tips: number }>>();
    for (const t of tips) {
      if (!m.has(t.date)) m.set(t.date, new Map());
      m.get(t.date)!.set(t.employee_id, {
        hours_worked: Number(t.hours_worked),
        raw_tips: Number(t.raw_tips),
      });
    }
    return m;
  }, [tips]);

  const salesByDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of sales) m.set(s.date, Number(s.total_sales));
    return m;
  }, [sales]);

  const eventsByDate = useMemo(() => {
    const m = new Map<string, TipEvent[]>();
    for (const ev of events) {
      if (!m.has(ev.date)) m.set(ev.date, []);
      m.get(ev.date)!.push(ev);
    }
    return m;
  }, [events]);

  // Per-date map: eventName → empId → amount
  const kitchenTipoutsByDate = useMemo(() => {
    const m = new Map<string, Map<string, Map<string, number>>>();
    for (const kt of kitchenTipoutRecords) {
      if (!m.has(kt.date)) m.set(kt.date, new Map());
      const dateMap = m.get(kt.date)!;
      const evName = kt.source ?? "";
      if (!dateMap.has(evName)) dateMap.set(evName, new Map());
      if (kt.employee_id) dateMap.get(evName)!.set(kt.employee_id, kt.amount);
    }
    return m;
  }, [kitchenTipoutRecords]);

  async function handleDaySave(
    date: string,
    data: {
      tips: Array<{ employee_id: string; hours_worked: number; raw_tips: number; calculated_tips: number }>;
      totalSales: number;
      events: Array<{ id?: string; name: string; total_gratuity: number; kitchen_tipout: number }>;
      kitchenTipouts: Array<{ eventName: string; entries: KitchenTipEntry[] }>;
      deletedEventIds: string[];
    },
  ) {
    setError(null);
    try {
      await Promise.all([
        upsertDailyTipsBatch(
          data.tips.map((t) => ({ date, ...t })),
        ),
        data.totalSales > 0 ? upsertDailySales(date, data.totalSales) : Promise.resolve(),
        ...data.events.map((ev) =>
          upsertEvent({ id: ev.id, date, name: ev.name, total_gratuity: ev.total_gratuity, kitchen_tipout: ev.kitchen_tipout }),
        ),
        ...data.kitchenTipouts.map((kt) =>
          replaceKitchenTipoutsForEvent(date, kt.eventName, kt.entries),
        ),
        ...data.deletedEventIds.map((id) => deleteEvent(id)),
      ]);
      await loadPeriodData();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function dayLabel(dateStr: string): string {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  }

  function dayHasData(dateStr: string): boolean {
    const dayTips = tipsByDate.get(dateStr);
    if (dayTips) {
      for (const t of dayTips.values()) {
        if (t.hours_worked > 0 || t.raw_tips > 0) return true;
      }
    }
    return (salesByDate.get(dateStr) ?? 0) > 0;
  }

  if (loading) {
    return <div className="p-6 text-sm text-tribal-muted">Loading…</div>;
  }

  if (periods.length === 0) {
    return (
      <div className="p-6 text-sm text-tribal-muted">
        No pay periods defined yet. Go to the <strong>Pay Periods</strong> tab to create one.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      {error && (
        <div className="mb-4 rounded-lg bg-red-900/20 px-3 py-2 text-sm text-red-400">{error}</div>
      )}

      {/* Period selector */}
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-tribal-muted">Pay Period</label>
        <select
          value={selectedId ?? ""}
          onChange={(e) => setSelectedId(e.target.value)}
          className="rounded-md border border-tribal-border bg-tribal-bg px-3 py-1.5 text-sm focus:border-tribal-red focus:outline-none"
        >
          {periods.map((pp) => (
            <option key={pp.id} value={pp.id}>
              {fmtRange(pp)} {pp.is_locked ? " (locked)" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        {/* Day grid */}
        <div className="space-y-2">
          {dates.map((date) => {
            const isExpanded = expandedDay === date;
            const hasData = dayHasData(date);
            return (
              <div key={date}>
                {isExpanded ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => setExpandedDay(null)}
                      className="mb-1 text-xs text-tribal-muted underline hover:text-white"
                    >
                      Collapse
                    </button>
                    <DayEditor
                      date={date}
                      employees={employees}
                      kitchenEmployees={kitchenEmployees}
                      existingTips={tipsByDate.get(date) ?? new Map()}
                      existingSales={salesByDate.get(date) ?? 0}
                      existingEvents={eventsByDate.get(date) ?? []}
                      existingKitchenTipouts={kitchenTipoutsByDate.get(date) ?? new Map()}
                      processingFeePct={feePct}
                      locked={selected?.is_locked ?? false}
                      onSave={(data) => handleDaySave(date, data)}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setExpandedDay(date)}
                    className={`w-full rounded-lg border px-4 py-2 text-left text-sm transition-colors ${
                      hasData
                        ? "border-tribal-border bg-tribal-surface hover:border-tribal-red/40"
                        : "border-dashed border-tribal-border/50 text-tribal-muted hover:border-tribal-border hover:bg-white/[0.02]"
                    }`}
                  >
                    <span className="font-medium">{dayLabel(date)}</span>
                    {hasData && (
                      <span className="ml-3 text-xs text-tribal-muted">
                        {money(
                          Array.from(tipsByDate.get(date)?.values() ?? []).reduce(
                            (s, t) => s + t.raw_tips,
                            0,
                          ),
                        )}{" "}
                        tips
                        {(salesByDate.get(date) ?? 0) > 0 && (
                          <> · {money(salesByDate.get(date)!)} sales</>
                        )}
                      </span>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary sidebar */}
        {selected && (
          <PayPeriodSummary
            employees={employees}
            tips={tips}
            sales={sales}
            startDate={selected.start_date}
            endDate={selected.end_date}
          />
        )}
      </div>
    </div>
  );
}

function money(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}
